import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Card, Dialog, DialogContent, DialogTitle, Grid, IconButton, Theme, Typography } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from 'src/components/button/Button';
import CurrencyFormat from 'src/components/currency/Currency';
import InputReference from 'src/components/InputReference';
import ParentBikeInModal from 'src/components/ParentBikeInModal';
import ProductAvailabilityLabel from 'src/components/productAvailabilityLabel';
import ProductImage from 'src/components/ProductImage';
import QuantitySelector from 'src/components/QuantitySelector';
import { ReturnInfoBox } from 'src/components/ReturnInfoBox';
import { AppModules } from 'src/services/api/models';
import RoleBaseAccess from 'src/services/RoleBaseAccess';
import Store from 'src/stores/RootStore';
import { TCartItem } from 'src/stores/types/CartItemModel';
import { TProductModel } from 'src/stores/types/ProductTypes';
import UserStore from 'src/stores/UserStore';
import CompalibleBikes from './CompatibleBikes';

interface ProductDetailProps extends WithStyles<typeof styles> {
    store?: typeof Store.Type;
    user?: typeof UserStore.Type;
    setCartTypeToSparePart: () => void;
}

const ProductDetail = inject(
    'store',
    'user',
)(
    observer(({ store, user, classes, setCartTypeToSparePart }: ProductDetailProps) => {
        const [quantity, setQuantity] = useState(1);
        const [reference, setReference] = useState('');
        const selectedProduct = store!.spareParts.getSelectedSparePart;
        const isOpen = store!.spareParts.isSparePartDetailDialogOpen;
        const parentBike = store!.spareParts.searchResult.parentBike;

        useEffect(() => {
            if (isOpen && selectedProduct) {
                selectedProduct.getCompatibleBikesByPartId();
            }
        }, [isOpen, selectedProduct]);

        const handleCloseDialog = () => {
            // close dialog
            store!.spareParts.toggleSparePartDetailDialog(false);
            // reset state
            setQuantity(1);
            setReference('');
        };

        const addProductToCart = (product: TProductModel) => {
            const newProduct = {
                id: `${product.id}`,
                type: product.type,
                product,
                battery: null,
                quantity,
                reference,
                parentBikeCode: product.parentCode || '',
            } as TCartItem;
            store!.cart.addCartItem(newProduct, quantity);
            setCartTypeToSparePart();
            handleCloseDialog();
        };

        const displaySparePart = (product: TProductModel): JSX.Element => {
            const price = product.getPrice();
            return (
                <Grid container>
                    <Grid item xs={12}>
                        <Card elevation={0} data-testid="spare-parts-img">
                            <ProductImage id={product.id} type={product.type} showThumbnail={false} />
                        </Card>
                    </Grid>
                    <Grid item xs={12} className={classes.info}>
                        <div className={classes.infoHeader}>
                            <Typography
                                className={classes.title}
                                gutterBottom
                                variant="h6"
                                data-testid="spare-parts-title"
                            >
                                {product.translatedDescription(user!.preferredLang)}
                            </Typography>
                            {price !== null && (
                                <div className={classes.price}>
                                    <Typography variant="h4" color="primary" data-testid="spare-parts-price">
                                        <CurrencyFormat value={price} />
                                    </Typography>
                                </div>
                            )}
                            {product.productInformation.inStock && <ProductAvailabilityLabel
                                inStock={product.productInformation.inStock}
                                availableFrom={product.productInformation.availableFrom}
                                stockQuantity={product.productInformation.stockQuantity}
                            />}
                        </div>

                        {!!product.compatibleBikesNameList.length && (
                            <CompalibleBikes bikesList={product.compatibleBikesNameList as any} />
                        )}

                        <ReturnInfoBox isReturn={false} productCode={product.id} />

                        {RoleBaseAccess.isModuleAllowed(AppModules.ORDER_PRODUCTS) && (
                            <>
                                {!store!.spareParts.isSparePartDetailDialogFromCart ? (
                                    <div className={classes.actions}>
                                        <div className={classes.itemAction}>
                                            <Grid container spacing={3}>
                                                <Grid item lg={12} sm={12}>
                                                    <InputReference
                                                        reference={reference}
                                                        updateReference={setReference}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </div>
                                        <div className={classes.itemAction}>
                                            <div className={classes.quantity}>
                                                <QuantitySelector
                                                    quantity={quantity}
                                                    maxQuantity={product.productInformation.stockQuantity}
                                                    updateQuantity={setQuantity}
                                                />
                                            </div>
                                        </div>

                                        <Grid container spacing={3} className={classes.buttons}>
                                            <Grid item xs={12} sm={6}>
                                                <Button
                                                    onClick={handleCloseDialog}
                                                    variant="outlined"
                                                    fullWidth
                                                    className={classes.button}
                                                    data-testid="spare-parts-cancel-button"
                                                >
                                                    <FormattedMessage id="returns.form_label_cancel" />
                                                </Button>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Button
                                                    data-testid="add-spare-part-button"
                                                    disabled={!product.productInformation.stockQuantity}
                                                    className={classes.button}
                                                    color="primary"
                                                    variant="contained"
                                                    fullWidth
                                                    onClick={() => addProductToCart(product)}
                                                >
                                                    <FormattedMessage id="dialog.addToCart" />
                                                    <AddIcon />
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </div>
                                ) : (
                                    RoleBaseAccess.isModuleAllowed(AppModules.ORDER_PRODUCTS) && (
                                        <div className={classes.actions}>
                                            <div className={classes.buttons}>
                                                <Button
                                                    data-testid="sparePart-detail-close"
                                                    onClick={handleCloseDialog}
                                                    variant="contained"
                                                    color="primary"
                                                >
                                                    <FormattedMessage id="dialog.backToCart" />
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            );
        };

        return (
            <Dialog
                open={isOpen}
                scroll="body"
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
                aria-labelledby="product-detail-dialog-title"
                data-testid="sparePart-detail-dialog"
            >
                <DialogTitle id="product-detail-dialog-top">
                    <Typography data-testid="product-detail-dialog-title" variant="h5">{`# ${
                        store!.spareParts.selectedSparePartId
                    }`}</Typography>

                    {parentBike && <ParentBikeInModal bike={parentBike} />}

                    <IconButton
                        aria-label="Close"
                        className={classes.closeButton}
                        onClick={handleCloseDialog}
                        data-testid="close-sparePart-dialog"
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedProduct ? (
                        displaySparePart(selectedProduct)
                    ) : (
                        <FormattedMessage id="dialog.productNotFound" />
                    )}
                </DialogContent>
            </Dialog>
        );
    }),
);

function styles({ spacing, palette }: Theme) {
    return createStyles({
        info: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            marginTop: '1rem',
        },
        infoHeader: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',

            '& > svg': {
                marginRight: spacing(1),
            },
        },
        title: {
            width: '100%',
        },
        price: {
            flex: 1,
        },
        actions: {
            display: 'flex',
            flexDirection: 'column',
            margin: '1rem 0',
        },
        itemAction: {
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: '2rem',
        },
        quantity: {
            display: 'flex',
            flex: 1,
        },
        buttons: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
        button: {
            padding: '1em 2em',
            justifyContent: 'space-between',
        },
        closeButton: {
            position: 'absolute',
            right: spacing(1),
            top: spacing(1),
            color: palette.grey[500],
        },
    });
}

export default withStyles(styles)(ProductDetail);
